import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { company } from '~/company/Company';
import { membershipStore, userStore } from '~/stores';

import { clubEntryShown } from '../../domain/club';
import { saveClubEntryHint } from './clubEntryHint';

/**
 * Loads `/me` and the zone's plans once per session for a signed-in customer
 * of a company with the club, after the first page mounted; a guest gets the
 * zone's plans only (the home banner). The header
 * button, the menu badge and every entry point read the result; none of them
 * sends a request of its own. A failed first load is retried once, on the
 * next page change, so one network blip does not hide the club for the whole
 * session. Remembers whether the entry points showed, so the next visit
 * reserves their space only when they are likely to show.
 */
export const ClubSession = observer(() => {
  const isEnabled = company.isMembershipsEnabled;
  const isSignedIn = userStore.isAuthorized;
  const isAllowed = isEnabled && isSignedIn;
  const { pathname } = useLocation();
  const failedOn = useRef<string | null>(null);
  const isRetried = useRef(false);
  const isFailed = membershipStore.me.status === 'failed';
  const shown = isAllowed ? clubEntryShown(membershipStore.view) : null;
  const customerId = userStore.personalData.id;

  useEffect(() => {
    if (isEnabled && !isSignedIn) {
      void membershipStore.ensureGuestLoaded();
    }
  }, [isEnabled, isSignedIn]);

  useEffect(() => {
    if (!isAllowed) {
      return;
    }
    if (!isFailed) {
      void membershipStore.ensureLoaded();
      return;
    }
    if (failedOn.current === null) {
      failedOn.current = pathname;
      return;
    }
    if (!isRetried.current && failedOn.current !== pathname) {
      isRetried.current = true;
      void membershipStore.load();
    }
  }, [isAllowed, isFailed, pathname]);

  useEffect(() => {
    if (shown !== null) {
      saveClubEntryHint(customerId, shown);
    }
  }, [customerId, shown]);

  return null;
});
